import { defineMessages, type MessageDescriptor } from 'react-intl';

import { PartnerTemplatePartner } from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';

import type { DocumentType } from './types';

export const templatesMessages = defineMessages({
  documentTemplates: {
    id: 'templates.documentTemplates',
    defaultMessage: 'Document Templates',
  },
  encounterSchemas: {
    id: 'templates.encounterSchemas',
    defaultMessage: 'Encounter Schemas',
  },
  statusDraft: {
    id: 'templates.status.draft',
    defaultMessage: 'Draft',
  },
  statusCurrent: {
    id: 'templates.status.current',
    defaultMessage: 'Current',
  },
  statusObsolete: {
    id: 'templates.status.obsolete',
    defaultMessage: 'Obsolete',
  },
  statusDeleted: {
    id: 'templates.status.deleted',
    defaultMessage: 'Deleted',
  },
  typeSummary: {
    id: 'templates.type.summary',
    defaultMessage: 'Summary',
  },
  typeEvaluation: {
    id: 'templates.type.evaluation',
    defaultMessage: 'Evaluation',
  },
  typeFollowUpLetter: {
    id: 'templates.type.followUpLetter',
    defaultMessage: 'Follow-up Letter',
  },
  typeLiteratureReview: {
    id: 'templates.type.literatureReview',
    defaultMessage: 'Literature Review',
  },
  typeTemplate: {
    id: 'templates.type.template',
    defaultMessage: 'Template',
  },
  unknownPartner: {
    id: 'templates.partner.unknown',
    defaultMessage: 'Partner',
  },
});

// Keyed by the catalog's specialty value, so lookups use the raw backend string.
export const templateSpecialtyMessages = defineMessages({
  'General Practice': {
    id: 'templates.specialty.generalPractice',
    defaultMessage: 'General Practice',
  },
  'Family Medicine': {
    id: 'templates.specialty.familyMedicine',
    defaultMessage: 'Family Medicine',
  },
  Psychiatry: {
    id: 'templates.specialty.psychiatry',
    defaultMessage: 'Psychiatry',
  },
  Psychology: {
    id: 'templates.specialty.psychology',
    defaultMessage: 'Psychology',
  },
  Dentistry: {
    id: 'templates.specialty.dentistry',
    defaultMessage: 'Dentistry',
  },
  Physiotherapy: {
    id: 'templates.specialty.physiotherapy',
    defaultMessage: 'Physiotherapy',
  },
  Veterinary: {
    id: 'templates.specialty.veterinary',
    defaultMessage: 'Veterinary',
  },
  Cardiology: {
    id: 'templates.specialty.cardiology',
    defaultMessage: 'Cardiology',
  },
  Dermatology: {
    id: 'templates.specialty.dermatology',
    defaultMessage: 'Dermatology',
  },
  Neurology: {
    id: 'templates.specialty.neurology',
    defaultMessage: 'Neurology',
  },
  Orthopedics: {
    id: 'templates.specialty.orthopedics',
    defaultMessage: 'Orthopedics',
  },
  Pediatrics: {
    id: 'templates.specialty.pediatrics',
    defaultMessage: 'Pediatrics',
  },
  Surgery: {
    id: 'templates.specialty.surgery',
    defaultMessage: 'Surgery',
  },
  Other: {
    id: 'global.other',
    defaultMessage: 'Other',
  },
});

// Labels of scribe's template classification vocabulary
// (services/scribe/model/template_classification.go), keyed by id in display
// order; an id this client has no label for is filed under "Other".
export const templateDepartmentMessages: Record<string, MessageDescriptor> =
  defineMessages({
    'dental.general': {
      id: 'templates.department.dental.general',
      defaultMessage: 'General dentistry',
    },
    'dental.therapy': {
      id: 'templates.department.dental.therapy',
      defaultMessage: 'Therapy',
    },
    'dental.surgery': {
      id: 'templates.department.dental.surgery',
      defaultMessage: 'Surgery',
    },
    'dental.prosthodontics': {
      id: 'templates.department.dental.prosthodontics',
      defaultMessage: 'Prosthodontics',
    },
    'dental.orthodontics': {
      id: 'templates.department.dental.orthodontics',
      defaultMessage: 'Orthodontics',
    },
    'dental.periodontics': {
      id: 'templates.department.dental.periodontics',
      defaultMessage: 'Periodontics',
    },
    'dental.pediatric': {
      id: 'templates.department.dental.pediatric',
      defaultMessage: 'Pediatric dentistry',
    },
    'dental.hygiene': {
      id: 'templates.department.dental.hygiene',
      defaultMessage: 'Hygiene and prevention',
    },
  });

export const templateGroupMessages: Record<string, MessageDescriptor> =
  defineMessages({
    'dental.general.exam': {
      id: 'templates.group.dental.general.exam',
      defaultMessage: 'Check-ups',
    },
    'dental.general.emergency': {
      id: 'templates.group.dental.general.emergency',
      defaultMessage: 'Emergency care',
    },
    'dental.general.sedation': {
      id: 'templates.group.dental.general.sedation',
      defaultMessage: 'Sedation',
    },
    'dental.general.consultation': {
      id: 'templates.group.dental.general.consultation',
      defaultMessage: 'Consultations',
    },
    'dental.therapy.caries': {
      id: 'templates.group.dental.therapy.caries',
      defaultMessage: 'Caries',
    },
    'dental.therapy.endodontics': {
      id: 'templates.group.dental.therapy.endodontics',
      defaultMessage: 'Root canal treatment',
    },
    'dental.therapy.noncarious': {
      id: 'templates.group.dental.therapy.noncarious',
      defaultMessage: 'Non-carious lesions',
    },
    'dental.therapy.aesthetic': {
      id: 'templates.group.dental.therapy.aesthetic',
      defaultMessage: 'Aesthetics',
    },
    'dental.therapy.mucosa': {
      id: 'templates.group.dental.therapy.mucosa',
      defaultMessage: 'Oral mucosa diseases',
    },
    'dental.therapy.consultation': {
      id: 'templates.group.dental.therapy.consultation',
      defaultMessage: 'Consultations',
    },
    'dental.surgery.extraction': {
      id: 'templates.group.dental.surgery.extraction',
      defaultMessage: 'Tooth extraction',
    },
    'dental.surgery.implantation': {
      id: 'templates.group.dental.surgery.implantation',
      defaultMessage: 'Implantation',
    },
    'dental.surgery.bone_augmentation': {
      id: 'templates.group.dental.surgery.bone_augmentation',
      defaultMessage: 'Sinus lift and bone grafting',
    },
    'dental.surgery.operations': {
      id: 'templates.group.dental.surgery.operations',
      defaultMessage: 'Operations and complications',
    },
    'dental.surgery.consultation': {
      id: 'templates.group.dental.surgery.consultation',
      defaultMessage: 'Consultations',
    },
    'dental.prosthodontics.crowns_bridges': {
      id: 'templates.group.dental.prosthodontics.crowns_bridges',
      defaultMessage: 'Crowns and bridges',
    },
    'dental.prosthodontics.inlays_posts': {
      id: 'templates.group.dental.prosthodontics.inlays_posts',
      defaultMessage: 'Inlays and post-and-cores',
    },
    'dental.prosthodontics.veneers': {
      id: 'templates.group.dental.prosthodontics.veneers',
      defaultMessage: 'Veneers',
    },
    'dental.prosthodontics.implant_prosthetics': {
      id: 'templates.group.dental.prosthodontics.implant_prosthetics',
      defaultMessage: 'Implant prosthetics',
    },
    'dental.prosthodontics.removable': {
      id: 'templates.group.dental.prosthodontics.removable',
      defaultMessage: 'Removable dentures',
    },
    'dental.prosthodontics.impressions_splints': {
      id: 'templates.group.dental.prosthodontics.impressions_splints',
      defaultMessage: 'Impressions and splints',
    },
    'dental.prosthodontics.consultation': {
      id: 'templates.group.dental.prosthodontics.consultation',
      defaultMessage: 'Consultations',
    },
    'dental.orthodontics.diagnostics': {
      id: 'templates.group.dental.orthodontics.diagnostics',
      defaultMessage: 'Diagnostics',
    },
    'dental.orthodontics.braces': {
      id: 'templates.group.dental.orthodontics.braces',
      defaultMessage: 'Braces',
    },
    'dental.orthodontics.aligners': {
      id: 'templates.group.dental.orthodontics.aligners',
      defaultMessage: 'Aligners',
    },
    'dental.orthodontics.appliances': {
      id: 'templates.group.dental.orthodontics.appliances',
      defaultMessage: 'Appliances',
    },
    'dental.orthodontics.consultation': {
      id: 'templates.group.dental.orthodontics.consultation',
      defaultMessage: 'Consultations',
    },
    'dental.periodontics.exam': {
      id: 'templates.group.dental.periodontics.exam',
      defaultMessage: 'Examination',
    },
    'dental.periodontics.treatment': {
      id: 'templates.group.dental.periodontics.treatment',
      defaultMessage: 'Treatment',
    },
    'dental.periodontics.surgery': {
      id: 'templates.group.dental.periodontics.surgery',
      defaultMessage: 'Periodontal surgery',
    },
    'dental.periodontics.consultation': {
      id: 'templates.group.dental.periodontics.consultation',
      defaultMessage: 'Consultations',
    },
    'dental.pediatric.caries': {
      id: 'templates.group.dental.pediatric.caries',
      defaultMessage: 'Primary teeth caries',
    },
    'dental.pediatric.pulp': {
      id: 'templates.group.dental.pediatric.pulp',
      defaultMessage: 'Pulp treatment',
    },
    'dental.pediatric.extraction': {
      id: 'templates.group.dental.pediatric.extraction',
      defaultMessage: 'Extraction',
    },
    'dental.pediatric.prevention': {
      id: 'templates.group.dental.pediatric.prevention',
      defaultMessage: 'Adaptation and prevention',
    },
    'dental.pediatric.consultation': {
      id: 'templates.group.dental.pediatric.consultation',
      defaultMessage: 'Consultations',
    },
    'dental.hygiene.professional': {
      id: 'templates.group.dental.hygiene.professional',
      defaultMessage: 'Professional hygiene',
    },
    'dental.hygiene.prevention': {
      id: 'templates.group.dental.hygiene.prevention',
      defaultMessage: 'Prevention',
    },
  });

// Keyed by the partner enum; a partner this client does not know yet has no
// entry and reads as `templatesMessages.unknownPartner`.
export const partnerTemplateNameMessages: Partial<
  Record<PartnerTemplatePartner, MessageDescriptor>
> = defineMessages({
  [PartnerTemplatePartner.PartnerTemplatePartnerDentalPro]: {
    id: 'templates.partner.dentalPro',
    defaultMessage: 'DentalPRO',
  },
});

export const documentTypeMessages = defineMessages<DocumentType>({
  SUMMARY: { id: 'templateEditor.docTypeSummary', defaultMessage: 'Summary' },
  EVALUATION: {
    id: 'templateEditor.docTypeEvaluation',
    defaultMessage: 'Evaluation',
  },
  PROTOCOL: {
    id: 'templateEditor.docTypeProtocol',
    defaultMessage: 'Protocol',
  },
  LETTER: { id: 'templateEditor.docTypeLetter', defaultMessage: 'Letter' },
  FOLLOW_UP_LETTER: {
    id: 'templateEditor.docTypeFollowUpLetter',
    defaultMessage: 'Follow-up letter',
  },
  LITERATURE_REVIEW: {
    id: 'templateEditor.docTypeLiteratureReview',
    defaultMessage: 'Literature Review',
  },
});
